import { z } from "zod";
import { MachineProductEntitySchema } from "./machine-product";
import { SupplyInfoSchema } from "./machine-detail";

export const CreateMachineRequestSchema = z.object({
  routerKey: z.string(),
  companyId: z.number(),
  machineProductId: z.number(),
  ipAddress: z.string(),
  macAddress: z.string(),
});

export type CreateMachineRequest = z.infer<typeof CreateMachineRequestSchema>;

/**
 * 네트워크 없는 장비 등록 요청.
 *
 * IP·MAC 은 보내지 않는다 — 장비에 없는 값이라 서버가 대신 채운다.
 * (MAC 은 `SF:` 로 시작하는 합성값, IP 는 `0.0.0.0`)
 */
export const RegisterInstalledMachineRequestSchema = z.object({
  companyId: z.number(),
  routerId: z.number(),
  machineProductId: z.number(),
  alias: z.string().nullable().optional(),
});

export type RegisterInstalledMachineRequest = z.infer<
  typeof RegisterInstalledMachineRequestSchema
>;

export const MachineEntitySchema = z.object({
  id: z.number().nullable().optional(),
  routerId: z.number(),
  companyId: z.number(),
  machineProduct: MachineProductEntitySchema,
  ipAddress: z.string(),
  macAddress: z.string(),
  /** 운영자가 붙인 이름. 없으면 장비제품 이름으로 표시된다. */
  alias: z.string().nullable().optional(),
  /** MES 화면에서 감춤. 수집과 KPI 는 그대로 돈다. */
  hiddenInMes: z.boolean().optional(),
  status: z.enum(["OFFLINE", "WARMUP", "ONLINE", "WORKING", "WARNING"]),
  runtime: z.number().nullable().optional(),
  errorStates: z.array(
    z.enum([
      "LOW_PAPER",
      "NO_PAPER",
      "LOW_TONER",
      "NO_TONER",
      "DOOR_OPEN",
      "JAMMED",
      "OFFLINE",
      "SERVICE_REQUESTED",
      "INPUT_TRAY_MISSING",
      "OUTPUT_TRAY_MISSING",
      "MARKER_SUPPLY_MISSING",
      "OUTPUT_NEAR_FULL",
      "OUTPUT_FULL",
      "INPUT_TRAY_EMPTY",
      "OVERDUE_PREVENT_MAIN",
    ]),
  ),
});

export type MachineEntity = z.infer<typeof MachineEntitySchema>;

export const MachineStatusSchema = z.enum([
  "OFFLINE",
  "WARMUP",
  "ONLINE",
  "WORKING",
  "WARNING",
]);

export type MachineStatus = z.infer<typeof MachineStatusSchema>;

export const SuppliesDetailSchema = z.object({
  cyan: SupplyInfoSchema.optional().nullable(),
  magenta: SupplyInfoSchema.optional().nullable(),
  yellow: SupplyInfoSchema.optional().nullable(),
  black: SupplyInfoSchema.optional().nullable(),
});

export type SuppliesDetail = z.infer<typeof SuppliesDetailSchema>;

export const TrayDetailSchema = z.object({
  trayName: z.string().nullable().optional(),
  paperName: z.string().nullable().optional(),
  paperSize: z.string(),
  percentage: z.number().nullable().optional(),
  capacityStatus: z.string(),
  currentCapacity: z.number(),
  maxCapacity: z.number(),
});

export type TrayDetail = z.infer<typeof TrayDetailSchema>;

export const MachineDetailDtoSchema = z.object({
  machineId: z.number(),
  manufacturer: z.string(),
  /** 제품 모델명. 장비 사진을 찾는 데 쓰므로 별칭으로 덮어쓰면 안 된다. */
  modelName: z.string(),
  /** 화면에 보여줄 이름(별칭 우선). 이 값이 없는 서버와 붙으면 modelName 을 쓴다. */
  displayName: z.string().nullable().optional(),
  /** 장비 타입 (PRINTER/PLOTTER/CUTTER/BINDER 등) — 권 슬롯 플로터 제외 판정용 */
  machineType: z.string().optional(),
  ipAddress: z.string(),
  runtime: z.number().nullable().optional(),
  status: MachineStatusSchema,
  supplies: SuppliesDetailSchema,
  trays: z.array(TrayDetailSchema),
});

export type MachineDetailDto = z.infer<typeof MachineDetailDtoSchema>;

export const RouterInfoResponseSchema = z.object({
  id: z.number().nullable().optional(),
  routerName: z.string().nullable().optional(),
  companyId: z.number(),
  apiKey: z.string(),
  ipAddress: z.string().nullable().optional(),
});

export type RouterInfoResponse = z.infer<typeof RouterInfoResponseSchema>;

export const RouterMachinesResponseSchema = z.object({
  router: RouterInfoResponseSchema,
  machines: z.array(MachineDetailDtoSchema),
});

export type RouterMachinesResponse = z.infer<
  typeof RouterMachinesResponseSchema
>;
